import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Download, FileText } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

type ViewInvoiceDialogProps = {
  renewalId: number;
  renewalDate: Date;
  isOpen: boolean;
  onClose: () => void;
  updateCounter?: number; // Used to force rerender when invoice is updated
};

export default function ViewInvoiceDialog({
  renewalId,
  renewalDate,
  isOpen,
  onClose,
  updateCounter = 0,
}: ViewInvoiceDialogProps) {
  // State to track if we're showing the uploaded file or the generated invoice
  const [showingUploadedFile, setShowingUploadedFile] = useState(false);
  // State to track the file type (PDF, PNG, etc.)
  const [fileType, setFileType] = useState("PDF");
  const { toast } = useToast();
  
  // Simulated invoice file data - in a real app this would come from the server
  // For demo purposes, we'll toggle between showing this and a PDF when updateCounter changes
  useEffect(() => {
    // In a real app, we would fetch the invoice data here
    // For demo purposes, we'll simulate a new file being used when the counter changes
    if (updateCounter > 0) {
      setShowingUploadedFile(true);
      // Detect file type from file name or mimetype
      // This would normally come from the server's response
      const fileName = localStorage.getItem("lastUploadedInvoiceFileName") || "";
      const extension = fileName.split('.').pop()?.toLowerCase() || "";
      
      if (extension === "png" || extension === "jpg" || extension === "jpeg") {
        setFileType(extension.toUpperCase());
      } else {
        setFileType("PDF"); // Default to PDF if unknown
      }
    } else {
      setShowingUploadedFile(false);
      setFileType("PDF");
    }
  }, [updateCounter]);
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Invoice for Renewal - {formatDate(renewalDate)}</DialogTitle>
          <DialogDescription>
            View and download the invoice document for this renewal.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 max-h-[60vh] overflow-y-auto">
          {showingUploadedFile ? (
            // Show uploaded PDF preview (for demonstration)
            <div className="bg-white border rounded-md p-8 text-center">
              <FileText className="h-20 w-20 mx-auto text-red-500 mb-4" />
              <h3 className="font-semibold text-lg mb-2">New Invoice ({fileType})</h3>
              <p className="text-slate-600 mb-4">
                Uploaded: {new Date().toLocaleDateString()}
              </p>
              <div className="border-t border-slate-200 pt-4 mt-4">
                <p className="text-sm text-slate-500">Your newly uploaded {fileType} document</p>
                <p className="text-sm text-slate-500 mt-2">This document was just uploaded and replaces the previous invoice.</p>
              </div>
            </div>
          ) : (
            // Show default generated invoice
            <div className="bg-white border rounded-md p-8 shadow-sm">
              <div className="flex justify-between items-start mb-8">
                <div>
                  <h3 className="font-bold text-xl text-blue-700">SAASGAMA</h3>
                  <p className="text-sm text-slate-600">License Management Platform</p>
                  <p className="text-sm text-slate-600 mt-1">123 Tech Avenue</p>
                  <p className="text-sm text-slate-600">San Francisco, CA 94105</p>
                </div>
                <div className="text-right">
                  <h3 className="font-bold text-xl mb-1">INVOICE</h3>
                  <p className="text-sm text-slate-600">Invoice #: INV-{renewalId}-2025</p>
                  <p className="text-sm text-slate-600">Date: {formatDate(renewalDate)}</p>
                  <p className="text-sm text-slate-600">Due Date: {formatDate(new Date(renewalDate.getTime() + 14*24*60*60*1000))}</p>
                </div>
              </div>
              
              <div className="mb-6">
                <h4 className="font-semibold text-sm text-slate-700 mb-2">Bill To:</h4>
                <p className="text-sm">Acme Corporation</p>
                <p className="text-sm text-slate-600">1 Infinite Loop</p>
                <p className="text-sm text-slate-600">Cupertino, CA 95014</p>
                <p className="text-sm text-slate-600">billing@acmecorp.com</p>
              </div>
              
              <div className="mb-6">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 text-sm font-semibold text-slate-700">Description</th>
                      <th className="text-center py-2 text-sm font-semibold text-slate-700">Quantity</th>
                      <th className="text-right py-2 text-sm font-semibold text-slate-700">Unit Price</th>
                      <th className="text-right py-2 text-sm font-semibold text-slate-700">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-slate-200">
                      <td className="py-3 text-sm">Adobe Creative Cloud License Renewal</td>
                      <td className="py-3 text-sm text-center">1</td>
                      <td className="py-3 text-sm text-right">$99.00</td>
                      <td className="py-3 text-sm text-right">$99.00</td>
                    </tr>
                    <tr>
                      <td className="py-2" colSpan={2}></td>
                      <td className="py-2 text-sm font-semibold text-right">Subtotal</td>
                      <td className="py-2 text-sm text-right">$99.00</td>
                    </tr>
                    <tr>
                      <td className="py-2" colSpan={2}></td>
                      <td className="py-2 text-sm font-semibold text-right">Tax (8.5%)</td>
                      <td className="py-2 text-sm text-right">$8.42</td>
                    </tr>
                    <tr>
                      <td className="pt-3" colSpan={2}></td>
                      <td className="pt-3 text-base font-bold text-right border-t border-slate-200">Total</td>
                      <td className="pt-3 text-base font-bold text-right border-t border-slate-200">$107.42</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div className="mt-8 border-t border-slate-200 pt-6">
                <h4 className="font-semibold text-sm text-slate-700 mb-2">Payment Terms</h4>
                <p className="text-sm text-slate-600">Payment due within 14 days of invoice date.</p>
                <p className="text-sm text-slate-600 mt-2">Make all checks payable to SAASGAMA Inc.</p>
                <p className="text-sm text-slate-600 mt-4">If you have any questions about this invoice, please contact:</p>
                <p className="text-sm text-slate-600">billing@saasgama.com | (555) 123-4567</p>
              </div>
              
              <div className="mt-8 text-center">
                <p className="text-sm font-medium text-blue-600">Thank you for your business!</p>
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button 
            className="flex items-center"
            onClick={() => {
              // Simulate file download by creating a dummy download link
              const link = document.createElement('a');
              link.href = 'data:application/pdf;base64,JVBERi0xLjMKJcTl8uXrp/Og0MTGCjQgMCBvYmoKPDwgL0xlbmd0aCA1IDAgUiAvRmlsdGVyIC9GbGF0ZURlY29kZSA+PgpzdHJlYW0KeAFVjrsOgkAURHu+YppbQA6PXTQCm9gaKWxcOxsTQzTw/0tQLiC7mWROsvOWlAGDnACnEgKhY9LwdqrVQ+0Clt4jJ4jaE1PYWSEvpMnBxcTXNtdG9eqCbeQU9Z8x8q40BH1Ewl55QpBDFfzMcPdSNhK3ZTXHJ7qSG7cKZW5kc3RyZWFtCmVuZG9iago1IDAgb2JqCjExNQplbmRvYmoKMiAwIG9iago8PCAvVHlwZSAvUGFnZSAvUGFyZW50IDMgMCBSIC9SZXNvdXJjZXMgNiAwIFIgL0NvbnRlbnRzIDQgMCBSIC9NZWRpYUJveCBbMCAwIDMwMCAzMDBdCj4+CmVuZG9iago2IDAgb2JqCjw8IC9Qcm9jU2V0IFsgL1BERiAvVGV4dCBdIC9Db2xvclNwYWNlIDw8IC9DczEgNyAwIFIgPj4gL0ZvbnQgPDwgL1RUMSAxMCAwIFIgPj4gPj4KZW5kb2JqCjggMCBvYmoKPDwgL0xlbmd0aCA5IDAgUiAvTiAxIC9BbHRlcm5hdGUgL0Rldi';
              link.download = showingUploadedFile ? 'new_invoice.pdf' : 'original_invoice.pdf';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              
              toast({
                title: "Invoice Downloaded",
                description: `Downloaded invoice ${showingUploadedFile ? 'new_invoice.pdf' : 'original_invoice.pdf'}`,
              });
            }}
          >
            <Download className="mr-2 h-4 w-4" />
            Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}